import React, { useEffect, useState } from 'react';
import { Deal, Valuation } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow } from '../types/cashflow';
import { shortWon } from '../types/vehicle';
import { mult } from '../lib/metrics';
import { RISK_BAR, RISK_GRADES, RISK_STYLE, RiskGrade } from '../lib/risk';
import { riskApi } from '../services/riskApi';
import { vehicleApi } from '../services/vehicleApi';

/**
 * 재원 상세 보강 — factsheet 「재원 상세」 9개 중 투자목적 · 제한조건 · 산업분야 성과 · 리스크 분포.
 * 투자목적과 제한조건은 규약에서 읽는다(investment_vehicles.purpose · restrictions). 확인 전이면 비워 둔다.
 * 리스크는 건별 최신 **승인** 등급(/api/risk/latest-grades).
 */
export const FundProfile: React.FC<{
  vehicleId: number; deals: Deal[]; companies: Company[]; flows: Cashflow[]; values: Record<number, Valuation>;
}> = ({ vehicleId, deals, companies, flows, values }) => {
  const [terms, setTerms] = useState<{ purpose: string; limits: string[] } | null>(null);
  const [gradeOf, setGradeOf] = useState<Record<number, RiskGrade>>({});
  useEffect(() => {
    vehicleApi.get(vehicleId).then((v) => setTerms(v?.purpose ? { purpose: v.purpose, limits: v.restrictions ?? [] } : null)).catch(() => setTerms(null));
    riskApi.latestGrades().then(setGradeOf).catch(() => setGradeOf({}));
  }, [vehicleId]);
  const mine = deals.filter((d) => d.vehicleId === vehicleId && d.stage !== '검토');
  const open = (d: Deal) => d.stage !== '회수' && d.stage !== '상각';
  const rows = mine.map((d) => {
    const fs = flows.filter((f) => f.dealId === d.id);
    const inv = fs.filter((f) => f.kind === 'investment').reduce((s, f) => s + f.amount, 0);
    const ret = fs.filter((f) => f.kind === 'exit' || f.kind === 'dividend').reduce((s, f) => s + f.amount, 0);
    const val = open(d) ? (values[d.id]?.ourValue ?? 0) : 0;
    const grade: RiskGrade | '미평가' = gradeOf[d.id] ?? '미평가';
    return { d, inv, ret, val, grade, sector: companies.find((c) => c.id === d.companyId)?.sector ?? '미분류' };
  });
  const sectors = Array.from(new Set(rows.map((r) => r.sector))).map((s) => {
    const rs = rows.filter((r) => r.sector === s);
    const inv = rs.reduce((a, r) => a + r.inv, 0);
    return { s, inv, moic: inv ? rs.reduce((a, r) => a + r.ret + r.val, 0) / inv : null };
  });
  const held = rows.filter((r) => open(r.d));
  const heldInv = held.reduce((s, r) => s + r.inv, 0);
  const grades = ([...RISK_GRADES, '미평가'] as const)
    .map((g) => ({ g, amt: held.filter((r) => r.grade === g).reduce((s, r) => s + r.inv, 0) })).filter((x) => x.amt > 0);

  return (
    <div data-tour="profile" className="grid gap-4 lg:grid-cols-3 mb-5">
      <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900 mb-2">투자목적 · 제한조건</h2>
        {!terms ? <p className="text-sm text-slate-400 py-3">규약 확인 전</p> : (
          <>
            <p className="text-sm text-slate-800">{terms.purpose}</p>
            {terms.limits.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-slate-600 list-disc pl-4">{terms.limits.map((l) => <li key={l}>{l}</li>)}</ul>
            )}
          </>
        )}
      </section>
      <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900 mb-2">산업분야별 성과</h2>
        {sectors.length === 0 ? <p className="text-sm text-slate-400 py-3">투자 없음</p> : (
          <table className="w-full text-sm">
            <tbody>
              {sectors.map((x) => (
                <tr key={x.s} className="border-b border-slate-50 last:border-b-0">
                  <td className="py-1.5">{x.s}</td>
                  <td className="text-right tabular-nums text-slate-600">{shortWon(x.inv)}</td>
                  <td className={`text-right tabular-nums font-semibold ${x.moic !== null && x.moic >= 1 ? 'text-emerald-700' : 'text-red-600'}`}>{mult(x.moic)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900 mb-2">리스크 등급 분포</h2>
        {heldInv === 0 ? <p className="text-sm text-slate-400 py-3">보유 중인 건 없음</p> : (
          <>
            <div className="flex h-2.5 rounded-full overflow-hidden mb-2">
              {grades.map((x) => <div key={x.g} className={RISK_BAR[x.g]} style={{ width: `${(x.amt / heldInv) * 100}%` }} />)}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {grades.map((x) => <span key={x.g} className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_STYLE[x.g]}`}>{x.g} {shortWon(x.amt)}</span>)}
            </div>
          </>
        )}
      </section>
    </div>
  );
};
